import { useTranslation } from "react-i18next";
import { useCallback, useRef, type KeyboardEvent } from "react";
import { cn } from "../../utils/cn";

export type SegmentedOption = {
  id: string;
  label: string;
};

type SegmentedControlProps = {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Accessible label for the control group */
  ariaLabel?: string;
};

export const SegmentedControl = ({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: SegmentedControlProps) => {
  const { t } = useTranslation("common");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          break;
        case "Home":
          event.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          event.preventDefault();
          nextIndex = options.length - 1;
          break;
      }

      if (nextIndex !== null) {
        const nextOption = options[nextIndex];
        if (nextOption) {
          onChange(nextOption.id);
          // Focus the new tab
          const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
          buttons?.[nextIndex]?.focus();
        }
      }
    },
    [options, onChange]
  );

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel ?? t($ => $.timeRange)}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-border/70 bg-muted/80 p-1 shadow-subtle",
        className
      )}
    >
      {options.map((option, index) => {
        const isActive = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={cn(
              "px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded-md transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              isActive
                ? "bg-card text-foreground shadow-subtle border border-border"
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => onChange(option.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
